import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "aircraft-charter", label: "Aircraft Charter" },
  { value: "aircraft-for-sale", label: "Aircraft For Sale" },
  { value: "yachts-for-sale", label: "Yachts For Sale" },
  { value: "bullion", label: "Bullion & Commodities" },
  { value: "vistacoin", label: "Vistacoin (VSC)" },
  { value: "general", label: "General Enquiry" },
];

const initialForm = { name: "", email: "", phone: "", interest: "aircraft-charter", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const typeMap = { "aircraft-charter": "charter", "aircraft-for-sale": "aircraft", "yachts-for-sale": "yacht", bullion: "bullion", vistacoin: "vistacoin", general: "general" };
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: typeMap[form.interest] || "general",
          source: "form",
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          message: form.message,
          details: { interest: form.interest },
          consent: true,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      toast.success(`Enquiry received (ref ${data.reference}). A broker will be in touch within 2 hours during business hours.`);
      setForm(initialForm);
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors duration-300";
  const labelClass = "block text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-surface border-t border-white/5 scroll-mt-16"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">
              Contact
            </p>
            <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">
              Start the conversation
            </h2>
            <p className="mt-6 text-base text-white/70 font-light leading-relaxed max-w-md">
              Charter, acquisition, yacht or bullion — tell us what you need
              and a dedicated broker will respond personally.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            onSubmit={handleSubmit}
            data-testid="enquiry-form"
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            <div>
              <label htmlFor="enquiry-name" className={labelClass}>Name</label>
              <input
                id="enquiry-name"
                data-testid="enquiry-name-input"
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Your full name"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="enquiry-email" className={labelClass}>Email</label>
              <input
                id="enquiry-email"
                data-testid="enquiry-email-input"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="you@company.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="enquiry-phone" className={labelClass}>Phone (optional)</label>
              <input
                id="enquiry-phone"
                data-testid="enquiry-phone-input"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+44 ..."
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="enquiry-interest" className={labelClass}>I'm interested in</label>
              <select
                id="enquiry-interest"
                data-testid="enquiry-interest-select"
                value={form.interest}
                onChange={update("interest")}
                className={`${inputClass} appearance-none`}
              >
                {INTERESTS.map((i) => (
                  <option key={i.value} value={i.value} className="bg-surface text-white">
                    {i.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-message" className={labelClass}>Message</label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Route, dates, passengers — or the asset you're looking for."
                className={`${inputClass} resize-none`}
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="enquiry-submit-button"
                className="group inline-flex items-center gap-3 bg-gold text-black text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Send Enquiry"}
                <ArrowRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
